import { ApiResponse } from './api';
import { UserGroup } from './userGroup';

export enum UserStatus {
  REGISTERED = 'Registered',
  PENDING = 'Pending',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  BLOCKED = 'Blocked',
  MANUAL_CHECK_REQUIRED = 'Manual Check Required',
  WAITING_ALPACA_ACTIVATION = 'Waiting Alpaca Activation',
}

export interface UserStatusOption {
  id: number;
  name: UserStatus;
}

export type User = {
  id: number;
  fullName: string;
  nationalIdNumber: string;
  mobile: string;
  creationSrc: string;
  email: string;
  disableReason: string;
  approvalReason: string;
  errorMessage: string;
  status: UserStatusOption;
  group: UserGroup;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export type UserHistory = {
  id: number;
  userId: number;
  employeeId: number;
  prevValue: User;
  nextValue: User;
  createdAt: Date | string;
  updatedAt: Date | string;
};

interface UserTransactionActivity {
  id: number;
  activity: string;
  description_en: string;
  description_ar: string;
}

export type UserTransaction = {
  id: number;
  amount: number;
  activity: UserTransactionActivity;
  userId: number;
  details: IAnyObject;
  createdAt: Date | string;
};

/** Premium / subscription state for display */
export enum PremiumState {
  ACTIVE = 'ACTIVE',
  GRACE_ACTIVE = 'GRACE_ACTIVE',
  EXPIRED = 'EXPIRED',
}

/** Subscription plan billing interval */
export type SubscriptionPlan = 'Monthly' | 'Annual';

/** Display shape for a user subscription (premium state, plan, start/end timestamps) */
export type UserSubscriptionDisplay = {
  premiumState: PremiumState;
  plan: SubscriptionPlan;
  startDate: number; // timestamp (ms)
  endDate: number | null; // timestamp (ms)
};

export type UserSubscription = {
  id: number;
  amount: number;
  startDate: Date | string | number;
  endDate: Date | string | number | null;
  endDateType: string;
  frequencyUnit: string;
  /** Premium state: ACTIVE | GRACE_ACTIVE | EXPIRED */
  status: string;
  registrationToken: string;
  merchantTransactionId: string;
  paymentBrand: string;
  last4Digits: string;
  registrationPayload: IAnyObject;
  isPrimaryCard: boolean;
  isDeleted: boolean;
  deletedAt: Date | string | null;
  canceledAt: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export enum BankType {
  VIRTUAL = 'VIRTUAL',
  OTHER = 'OTHER',
}

export type BankItemBankType = {
  id: number;
  description: string;
  type: BankType;
};

export type BankItemUserBankDetails = {
  id: number;
  accountNumber: string;
  amount: number;
  cashAllocationValue: number;
  beneficiaryAddress1: string;
  beneficiaryAddress2: string;
  beneficiaryName: string;
  hold: number;
  iban: string;
  swiftCode: string;
  unsettledFee: number;
};

export type UserBankInfo = {
  id: number;
  bankType: BankItemBankType;
  iban: string;
  isPrimaryAccount: boolean;
  swiftCode: string;
  userBankDetails: BankItemUserBankDetails;
};

export type UserVirtualB2BHistory = {
  id: number;
  userBankDetailsId: number;
  prevAmount: number;
  prevHold: number;
  prevCashAllocationValue: number;
  prevUnsettledFee: number;
  prevWithdrawableAmount: number;
  amount: number;
  hold: number;
  cashAllocationValue: number;
  unsettledFee: number;
  withdrawableAmount: number;
  newAmount: number;
  newHold: number;
  newCashAllocationValue: number;
  newUnsettledFee: number;
  newWithdrawableAmount: number;
  metadata: IAnyObject;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export enum DocumentTypes {
  SELFIE = 'Selfie',
  VERIFICATION_DOCUMENT = 'Verification Document',
  ACCOUNT_STATEMENT = 'Account Statement',
  TRADE_CONFIRMATION = 'Trade Confirmation',
  OTHER = 'Other',
}

export type UserAttachmentDocumentType = {
  id: number;
  type: DocumentTypes;
};

export type UserAttachment = {
  id: number;
  documentLink: string;
  documentType: UserAttachmentDocumentType;
  createdAt: Date | string;
};

export enum UserSurveyResultStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface UserSurveyResultStatusOption {
  id: number;
  status: UserSurveyResultStatus;
  type: UserSurveyResultStatus;
}

export interface UserSurveyResultRiskGroupOption {
  id: number;
  type: string;
  status: string;
}

export type UserSurveyResult = {
  id: number;
  riskGroup: UserSurveyResultRiskGroupOption;
  status: UserSurveyResultStatusOption;
  createdAt: Date;
  updatedAt: Date;
};

export enum UserAgreementName {
  ACCOUNT_OPENING_AGREEMENT = 'account_opening_agreement',
  CHANGE_ALLOCATION_AGREEMENT = 'change_allocation_agreement',
}

export type UserAgreement = {
  id: number;
  name: UserAgreementName;
};

export type UserSignAgreement = {
  id: number;
  agreement: UserAgreement;
  createdAt: Date;
  updatedAt: Date;
};

export type UserWorldCheckScreenData = IAnyObject;

export type FetchUserResponse = ApiResponse<User>;
export type FetchUserWorldCheckScreenResponse = ApiResponse<UserWorldCheckScreenData>;
export type FetchUserSignAgreementsResponse = ApiResponse<{
  items: UserSignAgreement[];
  totalItems: number;
}>;
export type FetchUsersResponse = ApiResponse<{
  items: User[];
  totalItems: number;
}>;
export type FetchUserStatusOptionsResponse = ApiResponse<UserStatusOption[]>;
export type FetchUserAttachmentsDocumentTypeOptionsResponse = ApiResponse<UserAttachmentDocumentType[]>;
export type FetchUserHistoryResponse = ApiResponse<{
  items: UserHistory[];
  totalItems: number;
}>;
export type FetchUserTransactionsResponse = ApiResponse<{
  items: UserTransaction[];
  totalItems: number;
}>;
export type FetchUserBankInfoResponse = ApiResponse<UserBankInfo[]>;
export type FetchUserVirtualB2BHistoryResponse = ApiResponse<{
  items: UserVirtualB2BHistory[];
  totalItems: number;
}>;
export type FetchUserAttachmentsResponse = ApiResponse<UserAttachment[]>;
export type FetchUserSurveyResultsResponse = ApiResponse<{
  items: UserSurveyResult[];
  totalItems: number;
}>;

export type CreateUserAttachmentsRequest = {
  items: {
    documentTypeId: number;
    link: string;
  }[];
};
export type CreateUserAttachmentResponse = ApiResponse<UserAttachment>;

export type CreateUserRequest = Pick<User, 'mobile' | 'email'> & {
  status: number;
  group: number;
};
export type CreateUserResponse = ApiResponse<User>;

export type UpdateUserRequest = Omit<
  User,
  | 'status'
  | 'group'
  | 'disableReason'
  | 'approvalReason'
  | 'errorMessage'
  | 'nationalIdNumber'
  | 'email'
  | 'creationSrc'
  | 'createdAt'
  | 'updatedAt'
> & {
  status: number;
  group: number;
};
export type UpdateUserResponse = ApiResponse<{ message: string }>;

export type UpdateUserStatusRequest = Pick<User, 'id' | 'disableReason' | 'approvalReason'> & {
  status: UserStatus;
};
export type UpdateUserStatusResponse = ApiResponse<{ message: string }>;

export type WithdrawUserRequest = { id: number; amount: number };
export type WithdrawUserResponse = ApiResponse<{ message: string }>;

export type FetchUserSubscriptionsResponse = ApiResponse<{
  items: UserSubscription[];
  totalItems: number;
}>;

export type FetchUserSubscriptionResponse = ApiResponse<UserSubscription>;

/** Product/feature limit item in usage limits */
export type UserUsageLimitItem = {
  key: string;
  productTypeId?: number;
  limit?: number;
  usage?: number;
};

/** Limits nested in a plan usage record */
export type UserUsageLimits = {
  products?: UserUsageLimitItem[];
  features?: UserUsageLimitItem[];
};

/** Plan usage record from GET /user/{id}/usage (getAllUserUsage) */
export type UserUsage = {
  id: number;
  userId: number;
  name: string;
  status: string;
  description: string;
  key: string;
  applePlanKey: string;
  googlePlanKey: string;
  platform: string;
  limit: number;
  usage: number;
  expiresAt: string;
  limits: UserUsageLimits;
};

export type FetchUserUsageResponse = ApiResponse<UserUsage[]>;

export type TimelineDetails = {
  actualFees: number;
  actualAmount: number;
  paymentBrand: string;
  estimatedFees: number;
  estimatedAmount: number;
};

export type Timeline = {
  id: number;
  userId: number;
  status: string;
  isSubscription: boolean;
  type: string;
  amount: number;
  productNameEn: string;
  productNameAr: string;
  paymentType: string;
  detail: TimelineDetails;
};

export type FetchUserTimelinesResponse = ApiResponse<{
  items: Timeline[];
  totalItems: number;
}>;

export enum TimelineStatus {
  IN_PROGRESS = 'In Progress',
  PENDING = 'Pending',
  APPROVED = 'Approved',
  CANCELLED = 'Cancelled',
}

export enum PaymentType {
  'mada' = 'Mada',
  'master_card' = 'Master card',
  'visa' = 'Visa',
  'direct_transfer' = 'Direct transfer',
}

// export type NotesType = 'request' | 'event' | 'action' | 'other';

// export type Notes = {
//   id: number;
//   content: string;
//   userId: number;
//   creator: string;
//   type: NotesType;
//   createdAt: Date | string;
//   updatedAt: Date | string;
// };

// export type FetchNotesResponse = ApiResponse<{
//   items: Notes[];
//   totalItems: number;
// }>;
